/* eslint-disable react/function-component-definition, react/require-default-props */
import React from 'react';
import { Provider } from 'react-redux';
import { createStore, compose, applyMiddleware } from 'redux';
import PropTypes from 'prop-types';
import { StyledEngineProvider } from '@mui/material';

import 'regenerator-runtime/runtime'; // eslint-disable-line
import createSagaMiddleware from 'redux-saga';
// import { logger } from 'redux-logger';

import reducers from './reducers/index';
import sagas from './sagas/index';
import LayerInit from './layer_init';
import FN from './fn';
import { LIST_HOST_HOOK_CLASS } from './constants/list_graph';

// - - - store & middleware - - -
const sagaMiddleware = createSagaMiddleware();
const middlewares = [sagaMiddleware]; // logger

const store = compose(
  applyMiddleware(...middlewares),
)(createStore)(reducers);

try {
  sagaMiddleware.run(sagas);
} catch (error) {
  // Keep startup failure visible without crashing silently.
  console.error('[SpectraEditor] Failed to start sagas', error); // eslint-disable-line no-console
}

// - - - helper - - -
const ensureQuillDelta = (descs) => {
  const isArr = Array.isArray(descs);
  return isArr ? descs : [{ insert: descs }];
};

// - - - React - - -
// LC/MS: when `onLcmsPageRequest` is set, the host (e.g. ELN) must reload `multiEntities`
// with MS data for the requested RT/polarity. Triggers include `user_click`, `initial`,
// and `tic_polarity` (TIC polarity dropdown). The standalone demo in `src/index.js`
// implements a local mock via `buildLcmsStandaloneMultiEntities`.
const SpectraEditor = ({
  entity, others, cLabel, xLabel, yLabel,
  operations, forecast, molSvg, editorOnly, descriptions, exactMass,
  canChangeDescription, onDescriptionChanged,
  multiEntities, multiMolSvgs, entityFileNames, userManualLink,
  onLcmsPageRequest,
}) => (
  <Provider store={store}>
    <StyledEngineProvider injectFirst>
      <LayerInit
        entity={entity}
        multiEntities={multiEntities}
        entityFileNames={entityFileNames}
        userManualLink={userManualLink}
        others={others}
        cLabel={cLabel}
        xLabel={xLabel}
        yLabel={yLabel}
        forecast={forecast}
        operations={operations}
        descriptions={ensureQuillDelta(descriptions)}
        molSvg={molSvg}
        multiMolSvgs={multiMolSvgs}
        editorOnly={editorOnly}
        exactMass={exactMass}
        canChangeDescription={canChangeDescription}
        onDescriptionChanged={onDescriptionChanged}
        onLcmsPageRequest={onLcmsPageRequest}
      />
    </StyledEngineProvider>
  </Provider>
);

SpectraEditor.propTypes = {
  entity: PropTypes.object.isRequired,
  multiEntities: PropTypes.array,
  entityFileNames: PropTypes.array,
  others: PropTypes.object,
  cLabel: PropTypes.string,
  xLabel: PropTypes.string,
  yLabel: PropTypes.string,
  forecast: PropTypes.object,
  operations: PropTypes.array,
  descriptions: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  molSvg: PropTypes.string,
  multiMolSvgs: PropTypes.array,
  editorOnly: PropTypes.bool,
  canChangeDescription: PropTypes.bool,
  onDescriptionChanged: PropTypes.func,
  onLcmsPageRequest: PropTypes.func,
  userManualLink: PropTypes.object,
  exactMass: PropTypes.string,
};

SpectraEditor.defaultProps = {
  others: { others: [], addOthersCb: false },
  multiEntities: [],
  entityFileNames: [],
  cLabel: '',
  xLabel: '',
  yLabel: '',
  forecast: {},
  operations: [],
  descriptions: [],
  molSvg: '',
  exactMass: '',
  multiMolSvgs: [],
  editorOnly: false,
  canChangeDescription: false,
  userManualLink: {},
  onLcmsPageRequest: null,
};

export {
  SpectraEditor, FN, store,
  // Published DOM hook class names - see docs/architecture/frontend-architecture.md.
  // Exported here so a host can reference them programmatically instead of deep-importing
  // dist/constants/list_graph or hard-coding the literals, which is the coupling the
  // constant exists to remove.
  LIST_HOST_HOOK_CLASS,
};
